import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { WarningIcon } from "../Icons/Warning";
import { useAlert } from "../../context/AlertContext";
import { fetcher } from "../../utils/fetcher";
import { useTranslation } from "../../i18n";
import { AccountType } from "@/pages/Register";

interface RemoveAccountDialogProps {
  id: string;
  accountType: AccountType;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  onSuccessCallback?: () => void;
}

export const RemoveAccountDialog = ({
  id,
  accountType,
  dialogOpen,
  setDialogOpen,
  onSuccessCallback,
}: RemoveAccountDialogProps) => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  const handleAccountDelete = async () => {
    if (!id) return;

    const url =
      accountType === "volunteer" ? `/volunteer/${id}` : `/organisation/${id}`;

    try {
      await fetcher({
        url: url,
        method: "DELETE",
      });
      setAlert(true, "success", t("removeAccount.alerts.removed"));
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    } catch (error) {
      setAlert(
        true,
        "error",
        t("removeAccount.alerts.failed"),
      );
      console.error("Error removing account:", error);
    } finally {
      setDialogOpen(false);
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="xs"
      >
        <DialogTitle id="cancel-dialog-title">
          <WarningIcon />
          {t("removeAccount.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            {accountType === "volunteer"
              ? t("removeAccount.textVolunteer")
              : t("removeAccount.textOrganisation")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => {
              setDialogOpen(false);
            }}
          >
            {t("removeAccount.no")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            color="error"
            onClick={() => handleAccountDelete()}
          >
            {t("removeAccount.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
